/**
 * QualityFindingList — hallazgos del reporte de calidad (OT-0006), en un solo
 * formato para el diseñador, el diálogo de publicar y el detalle de una
 * metodología.
 *
 * El backend manda código + `params` (`app/services/template_quality.py`);
 * el texto sale de `templates:quality.findings.<code>`. `locale` y `field`
 * llegan como identificadores y aquí se traducen (`language`, `field`).
 */

import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AlertCircle, AlertTriangle, Info } from 'lucide-react'

import type { QualityFinding, QualitySeverity } from '../../../api/templates'

const SEVERITY_ORDER: QualitySeverity[] = ['blocking', 'error', 'warning']

const SEVERITY_STYLE: Record<QualitySeverity, { icon: typeof AlertCircle; className: string }> = {
  blocking: { icon: AlertCircle, className: 'text-critical' },
  error: { icon: AlertTriangle, className: 'text-high' },
  warning: { icon: Info, className: 'text-review' },
}

function sortFindings(findings: readonly QualityFinding[]): QualityFinding[] {
  return [...findings].sort(
    (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity),
  )
}

function countBySeverity(
  findings: readonly QualityFinding[],
): Record<QualitySeverity, number> {
  const counts: Record<QualitySeverity, number> = { blocking: 0, error: 0, warning: 0 }
  for (const finding of findings) counts[finding.severity] += 1
  return counts
}

/** "2 impiden publicar · 3 errores · 1 aviso", omitiendo los ceros. */
export function QualitySummary({ findings }: { findings: readonly QualityFinding[] }) {
  const { t } = useTranslation('templates')
  const counts = countBySeverity(findings)
  const parts = SEVERITY_ORDER.filter((s) => counts[s] > 0).map((severity) => (
    <span key={severity} className={SEVERITY_STYLE[severity].className}>
      {t(`quality.summary_${severity}`, { count: counts[severity] })}
    </span>
  ))
  if (parts.length === 0) return <span className="text-official">{t('quality.clean')}</span>
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5">
      {parts.flatMap((part, i) =>
        i === 0 ? [part] : [<span key={`sep-${i}`} className="text-ink-subtle">·</span>, part],
      )}
    </span>
  )
}

interface QualityFindingListProps {
  findings: readonly QualityFinding[]
  /** Con varias plantillas (metodología): agrupa por plantilla y enlaza a su diseñador. */
  templateIdByKey?: ReadonlyMap<string, string> | undefined
}

export function QualityFindingList({ findings, templateIdByKey }: QualityFindingListProps) {
  if (!templateIdByKey) return <FindingItems findings={findings} />
  return (
    <ul className="space-y-3">
      {groupByTemplate(findings).map(([templateKey, group]) => {
        const templateId = templateIdByKey.get(templateKey)
        return (
          <li key={templateKey}>
            <div className="flex flex-wrap items-baseline gap-x-2 mb-1 text-xs">
              {templateId ? (
                <Link
                  to="/templates/$templateId"
                  params={{ templateId }}
                  className="font-mono text-ink underline decoration-line-strong underline-offset-2 hover:text-brand"
                >
                  {templateKey}
                </Link>
              ) : (
                <span className="font-mono text-ink">{templateKey}</span>
              )}
              <span className="text-[11px]">
                <QualitySummary findings={group} />
              </span>
            </div>
            <div className="pl-3 border-l border-line">
              <FindingItems findings={group} />
            </div>
          </li>
        )
      })}
    </ul>
  )
}

/** Por plantilla, la más grave primero; dentro, lo que impide publicar primero. */
function groupByTemplate(findings: readonly QualityFinding[]): [string, QualityFinding[]][] {
  const groups = new Map<string, QualityFinding[]>()
  for (const finding of sortFindings(findings)) {
    const key = finding.template_key ?? ''
    groups.set(key, [...(groups.get(key) ?? []), finding])
  }
  return [...groups.entries()]
}

function FindingItems({ findings }: { findings: readonly QualityFinding[] }) {
  return (
    <ul className="space-y-1.5">
      {sortFindings(findings).map((finding, i) => (
        <FindingItem key={i} finding={finding} />
      ))}
    </ul>
  )
}

function FindingItem({ finding }: { finding: QualityFinding }) {
  const { t } = useTranslation(['templates', 'common'])
  const message = useFindingMessage(finding)
  const { icon: Icon, className } = SEVERITY_STYLE[finding.severity]
  return (
    <li className="flex items-start gap-2 text-xs text-ink">
      <Icon
        className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${className}`}
        aria-label={t(`quality.severity.${finding.severity}`)}
      />
      <span className="min-w-0">
        {message}
        {finding.location ? (
          <span className="ml-1 font-mono text-[10px] text-ink-subtle">
            {t('quality.location', { location: finding.location })}
          </span>
        ) : null}
      </span>
    </li>
  )
}

function useFindingMessage(finding: QualityFinding): string {
  const { t } = useTranslation(['templates', 'common'])
  const { locale, field, ...rest } = finding.params
  const params: Record<string, string | number> = { ...rest }
  if (typeof locale === 'string') params.language = t(`common:languages.${locale}`)
  if (typeof field === 'string') params.field = t(`quality.fields.${field}`, { defaultValue: field })
  return t(`quality.findings.${finding.code}`, {
    ...params,
    defaultValue: t('quality.unknown_finding', { code: finding.code }),
  })
}
